import { IconNames } from '@blueprintjs/icons';
import { ActionButtonPageTitle, Button } from '@strata/react-buttons';
import { FormContainer } from '@strata/react-form-container';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import {
    type ColumnDef,
    type ColumnDefGeneric,
    GridWithSearch,
    PrimaryData,
} from '@strata/react-grid';
import { TextInputFormik } from '@strata/react-inputs';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { type PrescriberListPrescriber, putPrescriberList } from 'api/auctionsAPI';
import { AddPrescribersDialog } from 'components/admin/prescriberLists//dialogs/AddPrescribersDialog';
import { DeletePrescriberDialog } from 'components/admin/prescriberLists/dialogs/DeletePrescriberDialog';
import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useGetPrescriberListsQuery } from 'store/api/auctionApi';

/** Column type to allow addition the actions column */
type PrescriberListColumn = PrescriberListPrescriber & { actions?: never };

const columns = ({
    userCanEdit,
    setPrescriberToDelete,
}: {
    userCanEdit: boolean;
    setPrescriberToDelete: Dispatch<SetStateAction<PrescriberListPrescriber | undefined>>;
}): ColumnDef<PrescriberListColumn>[] => [
    {
        accessorKey: 'name',
        header: 'Prescriber Name',
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'npi',
        header: 'NPI',
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    ...(userCanEdit
        ? [
              {
                  accessorKey: 'actions',
                  header: 'Actions',
                  enableSorting: false,
                  size: 50,
                  cell: ({ row }) => (
                      <div className="sds_grid-actions-row">
                          <Icon
                              icon={IconNames.TRASH}
                              intent={Icon.Intent.SECONDARY}
                              title="Delete"
                              onClick={() => setPrescriberToDelete(row.original)}
                          />
                      </div>
                  ),
              } as ColumnDefGeneric<PrescriberListColumn, 'actions'>,
          ]
        : []),
];

export const PrescriberListDetails = () => {
    const { prescriberListId } = useParams() as { prescriberListId: string };
    const navigate = useNavigate();
    const userCanEdit = usePermissionCheck(Permissions.WRITE_AUCTIONS);
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);

    const isCreate = prescriberListId === 'create';

    const { data: prescriberLists, isFetching, refetch } = useGetPrescriberListsQuery();
    const prescriberList = prescriberLists?.find(
        (list) => list.prescriberListId === prescriberListId,
    );

    useEffect(() => {
        if (!(isCreate || isFetching || prescriberList)) {
            handleError('Prescriber list not found.');
            navigate(AppRoutes.prescriberLists);
        }
    }, [isCreate, isFetching, prescriberList, navigate]);

    const [showAddPrescriberDialog, setShowAddPrescriberDialog] = useState(false);
    const [prescriberToDelete, setPrescriberToDelete] = useState<PrescriberListPrescriber>();

    const validationSchema = Yup.object({
        listName: Yup.string()
            .required(validationStrings.requiredField.errorMessage)
            // Check for unique name
            .test({
                test: (currentVal, { path, createError }) =>
                    prescriberLists?.some(
                        (list) =>
                            list.prescriberListId !== prescriberListId &&
                            list.listName === currentVal,
                    )
                        ? createError({
                              path,
                              message: `"${currentVal}" is used by another Prescriber List.`,
                          })
                        : true,
            }),
        prescribers: Yup.array(
            Yup.object({ npi: Yup.string().required(), name: Yup.string().required() }),
        )
            .required(validationStrings.requiredField.errorMessage)
            .min(1, validationStrings.requiredField.errorMessage),
    });

    return (
        <FormPageContainer>
            <FullscreenLoading
                isShowing={!isCreate && isFetching}
                loadingText="Loading prescriber lists."
            />
            <div
                className="flex__gap--medium padding__around--medium"
                style={{ flexDirection: 'column', height: '100%' }}
            >
                <ActionButtonPageTitle
                    label="Prescriber Lists"
                    title="Prescriber List Details"
                    id="prescriber-list-details-page-title-button"
                    onClick={() => navigate(AppRoutes.prescriberLists)}
                />
                <Formik
                    initialValues={
                        {
                            listName: prescriberList?.listName,
                            prescribers: prescriberList?.prescribers,
                        } as Yup.InferType<typeof validationSchema>
                    }
                    validationSchema={validationSchema}
                    enableReinitialize
                    onSubmit={async (values) => {
                        try {
                            await putPrescriberList({
                                npi: selectedNpi!,
                                prescriberListId: !isCreate ? prescriberListId : undefined,
                                ...values,
                            });
                            SuccessToast({
                                message: `Prescriber list successfully ${
                                    isCreate ? 'created' : 'updated'
                                }.`,
                            });
                            await refetch();
                            navigate(AppRoutes.prescriberLists);
                        } catch (err) {
                            handleError(`Error saving ${values.listName}.`, err as Error);
                        }
                    }}
                >
                    {(formik) => (
                        <div style={{ overflow: 'auto', flexGrow: 1 }}>
                            <FormContainer
                                formTitle="Prescriber List Configuration"
                                onFormSubmit={formik.handleSubmit}
                                onCancelClick={() => navigate(AppRoutes.prescriberLists)}
                                isSubmitting={
                                    !(formik.dirty && formik.isValid) || formik.isSubmitting
                                }
                                formLeftContent={
                                    <>
                                        <AddPrescribersDialog
                                            isOpen={showAddPrescriberDialog}
                                            onClose={() => setShowAddPrescriberDialog(false)}
                                        />
                                        <DeletePrescriberDialog
                                            prescriberToDelete={prescriberToDelete}
                                            onClose={() => setPrescriberToDelete(undefined)}
                                        />
                                        <TextInputFormik
                                            id="listName"
                                            label="Prescriber List Name"
                                            helperText="Will be used as the display name throughout this application when referring to this prescriber list."
                                            required
                                            disabled={!userCanEdit}
                                        />
                                        <div
                                            className="margin__top--medium margin__bottom--medium"
                                            style={{ overflow: 'auto', flexGrow: 1 }}
                                        >
                                            <GridWithSearch<PrescriberListColumn>
                                                data={formik.values.prescribers}
                                                sortProperties={[{ id: 'name', desc: false }]}
                                                rightHeaderContent={
                                                    <Button
                                                        label="Add Prescribers"
                                                        id="add-prescribers-button"
                                                        intent={Button.Intent.PRIMARY}
                                                        disabled={!userCanEdit}
                                                        onClick={() =>
                                                            setShowAddPrescriberDialog(true)
                                                        }
                                                    />
                                                }
                                                columns={columns({
                                                    userCanEdit,
                                                    setPrescriberToDelete,
                                                })}
                                            />
                                        </div>
                                    </>
                                }
                            />
                        </div>
                    )}
                </Formik>
            </div>
        </FormPageContainer>
    );
};
